/**
 * Realtime notifications via Pusher. Subscribes to private-user-{authUserId}
 * and triggers callbacks so dashboard stays in sync with extension/other tabs.
 */
import Pusher from "pusher-js";
import jwtDecode from "jwt-decode";
import axios from "@/axios";

const JOB_EVENTS = ["job.created", "job.updated", "job.deleted"];
const CREDITS_EVENTS = ["credits.updated"];
const RESUME_EVENTS = ["resume.generation.succeeded", "resume.generation.failed"];

let pusher = null;
let channel = null;

/**
 * Subscribe to realtime events for the current user.
 * @param {() => string} getToken - Function that returns the current JWT
 * @param {{ onJobs?: () => void, onCredits?: () => void }} callbacks
 * @returns {() => void} Unsubscribe function
 */
export function subscribeRealtime(getToken, callbacks = {}) {
  const token = getToken?.();
  if (!token) return () => { };

  let decoded;
  try {
    decoded = jwtDecode(token);
  } catch {
    return () => { };
  }
  const userId = decoded?.userId;
  if (!userId) return () => { };

  let cancelled = false;

  (async () => {
    try {
      const { data } = await axios.get("/api/notifications/config");
      if (!data?.key || cancelled) return;

      const baseUrl = axios.defaults.baseURL || "";
      pusher = new Pusher(data.key, {
        cluster: data.cluster || "us2",
        channelAuthorization: {
          endpoint: `${baseUrl}/api/notifications/auth`,
          transport: "ajax",
          params: {},
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      });

      if (cancelled) {
        pusher.disconnect();
        return;
      }

      const channelName = `private-user-${userId}`;
      channel = pusher.subscribe(channelName);

      const onJob = () => callbacks.onJobs?.();
      const onCredits = () => {
        callbacks.onCredits?.();
        callbacks.onJobs?.(); // credits may follow resume generation; refetch jobs too
      };

      JOB_EVENTS.forEach((event) => channel.bind(event, onJob));
      CREDITS_EVENTS.forEach((event) => channel.bind(event, onCredits));
      RESUME_EVENTS.forEach((event) => channel.bind(event, onJob));
    } catch (err) {
      // Realtime not configured or auth failed - ignore
      if (err?.response?.status !== 503) {
        console.warn("[realtime] subscribe failed:", err?.message);
      }
    }
  })();

  return () => {
    cancelled = true;
    if (channel) {
      JOB_EVENTS.concat(CREDITS_EVENTS, RESUME_EVENTS).forEach((event) => channel.unbind(event));
    }
    if (pusher) {
      pusher.disconnect();
      pusher = null;
    }
    channel = null;
  };
}
